import { useRef, useState, useCallback } from 'react';

/**
 * Bug 8686 — when Edit is clicked on a list row, master screens populate an
 * inline form that usually sits above the list. The viewport didn't move, so
 * nothing appeared to happen. This hook brings the form into view and briefly
 * highlights it so the edit is obvious.
 *
 * Usage:
 *   const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
 *   // attach to the form container:
 *   <div ref={formRef} style={{ ...existingStyle, ...editHighlightStyle(editHighlight) }}>
 *   // call from the edit handler after populating form state:
 *   scrollToForm();
 */
export default function useEditFormScroll() {
  const formRef = useRef(null);
  const [editHighlight, setEditHighlight] = useState(false);

  const scrollToForm = useCallback(() => {
    setEditHighlight(true);
    // Defer the scroll one tick so the form has populated/rendered first.
    setTimeout(() => {
      const el = formRef.current;
      if (!el) return;
      // The app has a sticky top header + nav bar (~100px). Without a scroll
      // margin, block:'start' parks the form behind that sticky chrome, so the
      // scroll looks like nothing happened. scrollMarginTop offsets for it.
      el.style.scrollMarginTop = '120px';
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    setTimeout(() => setEditHighlight(false), 2000);
  }, []);

  return { formRef, editHighlight, scrollToForm };
}

/**
 * Inline-style helper for the brief highlight (border glow). Spread onto the
 * form container's style so the highlight transitions in and out smoothly.
 */
export function editHighlightStyle(on) {
  return {
    transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
    ...(on
      ? {
          borderColor: 'var(--theme-primary)',
          boxShadow: '0 0 0 4px rgba(37, 99, 235, 0.15)',
        }
      : {}),
  };
}
